import React, { useCallback, useState } from "react";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Typography,
    CircularProgress,
} from "@mui/material";

import VoiceCommand from "./assign-command";
import { parseAssignmentCommand } from "./AiEngine";

interface AiAssignModalProps {
    open: boolean;
    onClose: () => void;
    users: any[];

    onAssign?: (assignments: any[]) => Promise<void>;
}

const AiAssignModal = ({ open, onClose, users, onAssign }: AiAssignModalProps) => {
    const [command, setCommand] = useState("");

    const [loading, setLoading] = useState(false);

    const [preview, setPreview] = useState<any>(null);

    /**
     * Keep callback stable.
     *
     * This prevents VoiceCommand from
     * restarting whenever transcript changes.
     */
    const handleVoiceResult = useCallback((text: string) => {
        setCommand(text);
    }, []);

    /**
     * Understand command
     */
    const handleParseCommand = () => {
        const text = command.trim();

        if (!text) {
            return;
        }

        const result = parseAssignmentCommand(text, users);

        console.log("🤖 PARSER RESULT:", result);

        if (!result.assignments.length) {
            alert(
                result.errors.length
                    ? result.errors.join("\n")
                    : 'Could not understand the command.\n\nExample:\n"Assign 162 to Adithya and 123 to Ramesh"'
            );

            return;
        }

        setPreview(result);
    };

    /**
     * Confirm assignment.
     *
     * IMPORTANT:
     * We are NOT calling the backend here yet.
     *
     * We will connect this to
     * updateUserHierarchy after confirming
     * the exact hierarchy structure.
     */
    const handleConfirm = async () => {
        if (!preview?.assignments?.length) {
            return;
        }

        const invalidUser = preview.assignments.some(
            (assignment: any) => !assignment.user || assignment.user.notFound
        );

        if (invalidUser) {
            return;
        }

        try {
            setLoading(true);

            if (onAssign) {
                await onAssign(preview.assignments);
            }

            setCommand("");
            setPreview(null);

            onClose();
        } catch (error) {
            console.error("❌ AI assignment failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCommand("");
        setPreview(null);
        setLoading(false);

        onClose();
    };

    /**
     * Total number of booths
     */
    const totalBooths = preview?.assignments?.length || 0;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-xl">
                        🤖
                    </div>

                    <div>
                        <Typography variant="h6" fontWeight={700}>
                            AI Booth Assignment
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Talk naturally — tell me which booth goes to whom
                        </Typography>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent>
                {!preview ? (
                    <div className="mt-3">
                        <Typography variant="body2" color="text.secondary" className="mb-2">
                            Try saying
                        </Typography>

                        <div className="mb-4 rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
                            "Assign 162 to Adithya and 123 to Ramesh"
                        </div>

                        <textarea
                            value={command}
                            onChange={(e) => setCommand(e.target.value)}
                            placeholder="Type or speak your assignment..."
                            className="w-full rounded-xl border border-gray-300 p-4 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            rows={4}
                        />

                        <VoiceCommand onResult={handleVoiceResult} />

                        <Button
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 2,
                                py: 1.3,
                                borderRadius: "12px",
                            }}
                            disabled={!command.trim()}
                            onClick={handleParseCommand}
                        >
                            Understand Command
                        </Button>
                    </div>
                ) : (
                    <div className="mt-3">
                        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5">
                            <Typography variant="body2" color="text.secondary">
                                🤖 I understood
                            </Typography>

                            <Typography variant="h6" fontWeight={700} className="mt-1">
                                Booth Assignments
                            </Typography>

                            <div className="mt-4 space-y-3">
                                {preview.assignments.map((assignment: any, index: number) => (
                                    <div
                                        key={`${assignment.booth}-${assignment.user?.id}-${index}`}
                                        className="rounded-xl bg-white p-4 shadow-sm"
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-100 font-bold text-indigo-600">
                                                    {assignment.booth}
                                                </div>

                                                <div>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Booth
                                                    </Typography>

                                                    <Typography variant="body1" fontWeight={700}>
                                                        {assignment.booth}
                                                    </Typography>
                                                </div>
                                            </div>

                                            <div className="text-right">
                                                <Typography variant="caption" color="text.secondary">
                                                    Assign to
                                                </Typography>

                                                <Typography variant="body1" fontWeight={700} color="primary">
                                                    {assignment.user?.name}
                                                </Typography>

                                                <Typography variant="caption" color="text.secondary">
                                                    Match {assignment.confidence}%
                                                </Typography>
                                            </div>
                                        </div>

                                        {assignment.spokenName !== assignment.user?.name && (
                                            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                                Heard as "{assignment.spokenName}" → matched to "
                                                {assignment.user?.name}"
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 rounded-xl bg-white p-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500">Total booths</span>

                                    <strong className="text-lg">{totalBooths}</strong>
                                </div>
                            </div>

                            {preview.errors?.length > 0 && (
                                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                                    {preview.errors.map((error: string, index: number) => (
                                        <p key={index} className="text-sm text-amber-700">
                                            ⚠️ {error}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>

            <DialogActions className="p-4">
                <Button onClick={handleClose}>Cancel</Button>

                {preview && preview.assignments?.length > 0 && (
                    <Button variant="contained" color="success" disabled={loading} onClick={handleConfirm}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : "Confirm Assignment"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AiAssignModal;
